import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import matchService from '../services/matchService';

const initialState = {
  matches: [],
  connections: [],
  invitations: [],
  pendingApprovals: [],
  userMatches: [],
  conversations: [],
  matchingStatus: null,
  pausedReason: '',
  learningProgress: 0,
  system: null,
  isLoading: false,
  isTriggering: false,
  isError: false,
  isSuccess: false,
  message: '',
};

export const triggerMatching = createAsyncThunk(
  'match/triggerMatching',
  async (_, { rejectWithValue }) => {
    try {
      return await matchService.triggerMatching();
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to trigger matching');
    }
  }
);

export const getMyMatches = createAsyncThunk('match/getMyMatches', async (_, { rejectWithValue }) => {
  try {
    const response = await matchService.getMyMatches();
    return response.data;
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to get matches');
  }
});

export const getConnections = createAsyncThunk(
  'match/getConnections',
  async (_, { rejectWithValue }) => {
    try {
      const response = await matchService.getConnections();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get connections');
    }
  }
);

export const getUserMatches = createAsyncThunk(
  'match/getUserMatches',
  async (status = null, { rejectWithValue }) => {
    try {
      const response = await matchService.getUserMatches(status);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get user matches');
    }
  }
);

export const getMatchConversations = createAsyncThunk(
  'match/getMatchConversations',
  async (matchId, { rejectWithValue }) => {
    try {
      const response = await matchService.getMatchConversations(matchId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get conversations');
    }
  }
);

export const acceptInvite = createAsyncThunk(
  'match/acceptInvite',
  async (matchId, { rejectWithValue }) => {
    try {
      const response = await matchService.acceptInvite(matchId);
      return { data: response, matchId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to accept invite');
    }
  }
);

export const rejectMatch = createAsyncThunk(
  'match/rejectMatch',
  async (matchId, { rejectWithValue }) => {
    try {
      const response = await matchService.rejectMatch(matchId);
      return { data: response, matchId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to reject match');
    }
  }
);

const matchSlice = createSlice({
  name: 'match',
  initialState,
  reducers: {
    resetMatchState: (state) => {
      state.isLoading = false;
      state.isTriggering = false;
      state.isError = false;
      state.isSuccess = false;
      state.message = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(triggerMatching.pending, (state) => {
        state.isTriggering = true;
        state.isError = false;
      })
      .addCase(triggerMatching.fulfilled, (state, action) => {
        state.isTriggering = false;
        state.isSuccess = true;
        const data = action.payload.data || action.payload;
        if (data?.status === 'learning') {
          state.matchingStatus = 'learning';
          state.pausedReason = '';
          state.learningProgress = data.learning_progress;
          state.system = data.system || null;
          state.matches = [];
          return;
        }
        if (data?.status === 'paused') {
          state.matchingStatus = 'paused';
          state.pausedReason = data.paused_reason || '';
          state.system = data.system || null;
          state.matches = Array.isArray(data.matches) ? data.matches : [];
          return;
        }

        state.matchingStatus = 'ready';
        state.pausedReason = '';
        state.system = data?.system || null;
        state.matches = Array.isArray(data) ? data : Array.isArray(data?.matches) ? data.matches : [];
      })
      .addCase(triggerMatching.rejected, (state, action) => {
        state.isTriggering = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getMyMatches.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getMyMatches.fulfilled, (state, action) => {
        state.isLoading = false;
        state.matches = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getMyMatches.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getConnections.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getConnections.fulfilled, (state, action) => {
        state.isLoading = false;
        state.connections = action.payload.connected || [];
        state.invitations = action.payload.invitations || [];
        state.pendingApprovals = action.payload.pendingApprovals || [];
      })
      .addCase(getConnections.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getUserMatches.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserMatches.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userMatches = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getUserMatches.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getMatchConversations.fulfilled, (state, action) => {
        state.conversations = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(acceptInvite.fulfilled, (state, action) => {
        state.invitations = state.invitations.filter((item) => item.matchId !== action.payload.matchId);
      })
      .addCase(rejectMatch.fulfilled, (state, action) => {
        state.matches = state.matches.filter((item) => item.matchId !== action.payload.matchId);
        state.invitations = state.invitations.filter((item) => item.matchId !== action.payload.matchId);
        state.pendingApprovals = state.pendingApprovals.filter(
          (item) => item.matchId !== action.payload.matchId
        );
      });
  },
});

export const { resetMatchState } = matchSlice.actions;
export default matchSlice.reducer;
